import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../config/url";
import * as XLSX from "xlsx";

const ROWS_PER_PAGE = 10;

// ---------- HELPERS ----------
const safeText = (v) =>
  v === null || v === undefined || String(v).trim() === "" ? "N/A" : String(v);

const lower = (v) => safeText(v).toLowerCase();

const formatINR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "0";
  return new Intl.NumberFormat("en-IN").format(num);
};

const formatDateIN = (d) => {
  if (!d) return "N/A";
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? "N/A" : x.toLocaleString("en-IN");
};

const pickPaymentId = (p) =>
  p?.paymentId ||
  p?.razorpayPaymentId ||
  p?.razorpay_payment_id ||
  p?.orderCreationId ||
  p?._id;

const pickSellerId = (p) =>
  p?.sellerId?._id || p?.sellerId || p?.seller?._id;

const pickSellerName = (p) =>
  p?.sellerName ||
  p?.seller?.name ||
  p?.sellerId?.name ||
  "N/A";

const pickAmount = (p) =>
  p?.amount ||
  p?.totalAmount ||
  p?.price ||
  0;

const normalizeStatus = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("success") || s.includes("complete") || s === "paid")
    return "Completed";
  if (s.includes("pending")) return "Pending";
  if (s.includes("fail")) return "Failed";
  return "Pending";
};

const SellerRegistrationPaymentsPage = () => {
  const [allPayments, setAllPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(null); // ✅ modal

  // ---------- FETCH ----------
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");

        const res = await axios.get(
          `${baseurl}/api/admin/registration/payments`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const list = Array.isArray(res.data?.paymentDetails)
          ? res.data.paymentDetails
          : Array.isArray(res.data)
          ? res.data
          : [];

        setAllPayments(list);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  // ---------- FILTER ----------
  const filteredPayments = useMemo(() => {
    const q = searchTerm.toLowerCase();

    return allPayments.filter(
      (p) =>
        lower(pickSellerName(p)).includes(q) ||
        lower(pickPaymentId(p)).includes(q)
    );
  }, [allPayments, searchTerm]);

  // ---------- PAGINATION ----------
  const totalPages = Math.max(
    1,
    Math.ceil(filteredPayments.length / ROWS_PER_PAGE)
  );

  const currentRows = filteredPayments.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  // =========================
  // ✅ EXCEL DOWNLOAD
  // =========================
  const downloadExcel = () => {
    if (!filteredPayments.length) return;

    const data = filteredPayments.map((p) => ({
      "Payment ID": safeText(pickPaymentId(p)),
      Seller: safeText(pickSellerName(p)),
      Amount: pickAmount(p),
      Status: normalizeStatus(p.paymentStatus || p.status),
      Date: formatDateIN(p.createdAt),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Seller Payments");

    XLSX.writeFile(wb, `Seller_Payments_${Date.now()}.xlsx`);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-bold">Seller Payments</h1>

        {/* ✅ DOWNLOAD BUTTON */}
        <button
          onClick={downloadExcel}
          className="bg-green-600 text-white px-4 py-2 rounded"
        >
          ⬇ Download Excel
        </button>
      </div>

      <input
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="border p-2 mb-4 w-full"
      />

      {loading ? (
        <div>Loading...</div>
      ) : (
        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th>ID</th>
              <th>Seller</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Status</th>
              <th>Invoice</th>
            </tr>
          </thead>

          <tbody>
            {currentRows.map((p, i) => {
              const status = normalizeStatus(
                p.paymentStatus || p.status
              );

              return (
                <tr
                  key={i}
                  className="text-center border-t hover:bg-blue-50 cursor-pointer"
                  onClick={() => setSelected(p)}
                >
                  <td>{safeText(pickPaymentId(p))}</td>
                  <td>{safeText(pickSellerName(p))}</td>
                  <td>₹{formatINR(pickAmount(p))}</td>
                  <td>{formatDateIN(p.createdAt)}</td>

                  <td>{status}</td>

                  <td>
                    <button className="bg-blue-500 text-white px-2 py-1 rounded">
                      Download
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ===================== */}
      {/* ✅ MODAL CARD */}
      {/* ===================== */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">

          <div
            className="absolute inset-0"
            onClick={() => setSelected(null)}
          />

          <div className="relative bg-white w-full max-w-md rounded-xl shadow-xl p-6">

            <div className="flex justify-between mb-4">
              <h3 className="font-bold">Payment Details</h3>
              <button onClick={() => setSelected(null)}>✕</button>
            </div>

            <div className="space-y-2 text-sm">
              <div><b>Payment ID:</b> {pickPaymentId(selected)}</div>
              <div><b>Seller:</b> {pickSellerName(selected)}</div>
              <div><b>Amount:</b> ₹{formatINR(pickAmount(selected))}</div>
              <div><b>Status:</b> {normalizeStatus(selected.paymentStatus)}</div>
              <div><b>Date:</b> {formatDateIN(selected.createdAt)}</div>
            </div>

          </div>
        </div>
      )}

      {/* PAGINATION */}
      <div className="mt-4 flex justify-between">
        <button onClick={() => setCurrentPage((p) => p - 1)}>Prev</button>
        <span>{currentPage} / {totalPages}</span>
        <button onClick={() => setCurrentPage((p) => p + 1)}>Next</button>
      </div>
    </div>
  );
};

export default SellerRegistrationPaymentsPage;